<script setup lang="ts">
import type { Usp } from '#types'

definePageMeta({
    contentPage: 'slots-lobby',
    heroPage: 'slots',
    pageSlug: 'slots',
    termsShown: true,
    enablePersonalizedGameHero: true,
})

const { site } = useCoreConfig()
const { isLoggedIn, isExisting } = useUserInfo()
const { isHydrating } = useNuxtApp()
const { useGetDepositSuccessfulCount } = usePaymentsClient()

const layoutContext = useLayoutContext()

const depositQuery = useGetDepositSuccessfulCount({ server: true, lazy: false })
const pageQuery = usePageData('slots-lobby')

// Block on SSR for SEO and hydration correctness;
// on client-side navigation, render immediately — data fills in reactively
if (import.meta.server || isHydrating) {
    await Promise.all([depositQuery, pageQuery])
}
else {
    layoutContext.hero.change({ page: 'slots' })
}

const { isFirstDeposit } = depositQuery
const { data: pageData } = pageQuery

const showWinnersSidebar = computed(() => {
    if (!isLoggedIn.value) return true

    return isFirstDeposit.value
})

// SEO - START

const { getHead, getSeoMeta } = useContentSeo(
    pageData.value?.seo || {
        metaTitle: `Online Slots | Play Progressive Jackpot Slots at ${site.title}`,
        metaDescription: `${site.title} is packed with unique slots, exclusive bonus games & huge progressive jackpots. Sign-up now. T&Cs apply. 18+. Play responsibly.`
    }
)

useHead(getHead())
useSeoMeta(getSeoMeta())
// SEO - END

const uspsAfterPopularGames: Array<Usp> = [
    {
        title: `Discover unique UK slots only at ${site.title}`,
        description: `Welcome to ${site.title}, your destination for exclusive slots in the UK. We provide a unique selection of online slots that can't be found anywhere else. Whether you're a fan of classic styles or prefer the fun of retro video slots, we have something exclusively for you. Dive into our immersive video content and get a feel for the fun that awaits.`,
        media: {
            type: 'video',
            src: 'https://media.platego.io/slots_lobby/16-9_Happytiger_showreel_-_Oktober.mp4?v=1',
            poster: '/slots_lobby/16-9_Happytiger_showreel_-_Oktober.jpg?v=2'
        }
    },
    {
        title: 'Our exclusive collection',
        description: `What sets our collection of UK slots online apart is not just their exclusivity, but also the fun progressive jackpots most of our games offer. We feature games from reputable developers, all of which are unique to ${site.title}. From traditional three-reel slots to the latest releases, each game provides a unique gaming experience that you won't find anywhere else. Check out our gameplay video to see some of these exciting features in action!`,
        media: {
            type: 'image',
            alt: 'Deep blue Casino slot machine',
            src: '/media/allslots/usps/2.png'
        }
    }
]

const uspsAfterGames: Array<Usp> = [
    {
        title: 'User-Friendly Gaming',
        description: `At ${site.title}, we believe in making online gaming accessible, enjoyable, and exclusive. That's why we've made our platform user-friendly and our games easy to understand. Whether you're a seasoned player or new to slots, you'll find our site easy to navigate and our games fun to play.`,
        media: {
            type: 'image',
            alt: 'Diamond Express mini game',
            src: '/media/allslots/usps/3.png'
        }
    },
    {
        title: 'Start Your Gaming Journey',
        description: `Are you ready? Dive into the world of exclusive online slots with progressive jackpots at ${site.title} today and discover your new favourite game. Remember, you won't find our titles anywhere else - this is the home of the most exclusive slots in the UK.`,
        media: {
            type: 'video',
            src: 'https://media.platego.io/slots_lobby/temple_of_the_sun_showcase_v1%20%281080p%29.mp4?v=1',
            poster: '/slots_lobby/temple_of_the_sun_showcase_v1.jpg?v=2'
        }
    }
]

const uspsAfterCTA: Array<Usp> = [
    {
        title: 'Experience the Fun',
        description: 'With our exclusive slots, every spin brings fun. Our gameplay videos give you a small taste of the fun, showing you just what you can expect when you play our exclusive slots.',
        media: {
            type: 'image',
            alt: 'Railroad Express mini game',
            src: '/media/allslots/usps/4.png'
        }
    },
    {
        title: `<a href="/bingo">Bingo Fun at ${site.title}</a>`,
        description: `In addition to our slots, we also offer a Bingo game at ${site.title}. This game, with its straightforward gameplay, is a favourite among players of all skill levels. Whether you're following along with the number calls or simply enjoying the game, our Bingo offering adds an extra layer to your ${site.title} experience. <a href="/bingo">Join the fun - Play Bingo now!</a>`,
        media: {
            type: 'image',
            alt: 'Bingo flash',
            src: '/media/allslots/usps/6.png'
        }
    },
    {
        title: `Join the ${site.title} Community`,
        description: `When you play at ${site.title}, you're becoming part of a vibrant community of gamers who share a passion for slots and bingo. Our active player chat allows you to connect with other players, exchange tips, and share your excitement. Sign up today to experience the best of UK slots and join a community that thrives on fun and camaraderie. We look forward to welcoming you to the ${site.title} family, where every spin and bingo call brings us closer together.`,
        media: {
            type: 'image',
            alt: 'Social chat',
            src: '/media/allslots/usps/7.png'
        }
    }
]
</script>

<template>
    <div class="slots">
        <GameListPersonalizedSections :fallback-categories="['SLOTS']">
            <template #after-first>
                <LazyWinnersRail
                    v-if="showWinnersSidebar"
                    title="Latest Winners"
                />

                <section v-if="!isLoggedIn && !isExisting && uspsAfterPopularGames?.length" class="section">
                    <div class="section__content">
                        <UspsSection :usps="uspsAfterPopularGames" />
                    </div>
                </section>
            </template>

            <template v-if="!isLoggedIn && !isExisting" #after-all>
                <section v-if="uspsAfterGames?.length || uspsAfterCTA?.length" class="section">
                    <div class="section__content">
                        <LazyVisibleUspsSection :usps="[...uspsAfterGames, ...uspsAfterCTA]" hydrate-on-visible />
                    </div>
                </section>
            </template>
        </GameListPersonalizedSections>

        <template v-if="!isLoggedIn && !isExisting">
            <section class="faq">
                <h2>FAQ</h2>

                <div class="faq__items">
                    <LazyVisibleUIExpandable show-toggle-icon hydrate-on-visible>
                        <template #header>
                            What makes {{ site.title }} unique?
                        </template>
                        <template #default>
                            {{ site.title }} offers a unique selection of exclusive online slots that can't be found anywhere else in the UK. Our games are designed to provide a fun and exciting gaming experience.
                        </template>
                    </LazyVisibleUIExpandable>
                </div>
            </section>
        </template>
    </div>
</template>

<style lang="scss" scoped>
.winners-head {
    text-align: left;
    font-size: $font-size-lg;
    font-weight: $text-bold;
    margin: 17px 0 10px;
}

.slots {
    display: flex;
    flex-direction: column;
    gap: 15px;

    @include tablet {
        gap: 20px;
    }

    :deep(.game-list) {
        margin: 0;
    }
}

.section {
    &_content {
        display: grid;
        gap: 3rem;
    }
}

.faq {
    &__items {
        :deep(.expandable) {
            color: var(--on-surface);
            background-color: var(--surface);
            padding: 12px;
        }

        :deep(.expandable__header) {
            color: var(--primary);
            font-size: $font-size-lg;
        }

        :deep(.expandable__toggle-icon) {
            color: var(--primary);
        }
    }
}
</style>
